import { ComponentProps, useMemo } from 'react';
import { styled, css } from 'styled-components';
import { useUpMedia, useIsBlockchainVersion } from '../../hooks';
import cr from '../../assets/img/cr-icon.png';
import eth from '../../assets/img/eth-icon.png';
import horseShoe from '../../assets/img/horse-shoe.png';
import { DesktopText, MobileText } from '../texts';

export type HeaderUserBalanceProps = {
  type: Currency['type'];
  balance: number;
} & ComponentProps<typeof Wrapper>;

const HeaderUserBalance = ({
  type,
  balance,
  ...props
}: HeaderUserBalanceProps) => {
  const blockchainVersion = useIsBlockchainVersion();
  const desktop = useUpMedia('md');

  const Value = desktop ? DesktopValue : MobileValue;

  balance = blockchainVersion ? parseFloat(balance.toFixed(5)) : balance;

  const icon = useMemo(() => {
    if (blockchainVersion) return eth;
    if (type) return iconsByCurrencyType[type];
  }, [type, blockchainVersion]);

  const renderBalance = useMemo(
    () => (blockchainVersion ? parseFloat(balance.toFixed(5)) : balance),
    [balance, blockchainVersion]
  );

  return (
    <Wrapper {...props}>
      {!!icon && <Icon src={icon} alt={'Currency icon'} />}
      <Value $type={blockchainVersion ? 'eth' : type}>{renderBalance}</Value>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  border-radius: 30px;
  padding: 2px 16px 2px 10px;
  background-color: ${({ theme }) => theme.getColor('bgCards')};
  ${({ theme }) => theme.getUpMedia('md')} {
    padding: ${({ theme }) => theme.spacing(2, 24, 2, 20)};
  }
`;

const Icon = styled.img`
  width: ${({ theme }) => theme.responsive(35)};
  height: ${({ theme }) => theme.responsive(35)};
  margin-right: ${({ theme }) => theme.responsive(10)};
`;

type ValueStyleType = 'eth' | Currency['type'];

type CommonValueStyleProps = {
  $type: ValueStyleType;
};

const colorStylesByType: {
  [key in NonNullable<ValueStyleType> | 'def']: ReturnType<typeof css>;
} = {
  eth: css`
    color: ${({ theme }) => theme.getColor('accentSecondary')};
  `,
  blockchain: css`
    color: ${({ theme }) => theme.getColor('accentSecondary')};
  `,
  tournament: css`
    color: ${({ theme }) => theme.getColor('mountainMeadow')};
  `,
  cr: css`
    color: ${({ theme }) => theme.getColor('completelyWhite')};
  `,
  def: css`
    color: ${({ theme }) => theme.getColor('accentSecondary')};
  `,
};

const commonStyles = css<CommonValueStyleProps>`
  line-height: 1;
  font-weight: 900;
  ${({ $type }) => colorStylesByType[$type ?? 'def']}
`;

const MobileValue = styled(MobileText)<CommonValueStyleProps>`
  ${commonStyles};
  font-size: 12px;
`;

const DesktopValue = styled(DesktopText)<CommonValueStyleProps>`
  ${commonStyles};
  font-size: ${({ theme }) => theme.responsive(22)};
`;

const iconsByCurrencyType: {
  [key in NonNullable<Currency['type']>]: string;
} = {
  cr: cr,
  tournament: horseShoe,
  blockchain: eth,
};

export { HeaderUserBalance };
